/**
 * ICP Options — Part 13 of Spec
 * 50 Industries (grouped by category) + 30 Personas (grouped by department)
 */

// ── Target Industries: 50 industries, 8 category groups ──
export const INDUSTRY_GROUPS = [
  {
    category: "Technology",
    options: ["SaaS", "FinTech", "E-commerce", "HealthTech", "EdTech", "Cybersecurity", "AI/ML", "DevTools", "MarTech", "HR Tech", "LegalTech", "PropTech", "InsurTech", "CleanTech", "AgTech", "Gaming", "AdTech", "DataTech"],
  },
  {
    category: "Financial Services",
    options: ["Financial Services (Banking/Credit)", "Asset Management", "Private Equity / Venture Capital", "Insurance", "Payments & Processing"],
  },
  {
    category: "Healthcare & Life Sciences",
    options: ["Healthcare / MedTech", "Pharma / Life Sciences", "Biotech", "Mental Health & Wellness"],
  },
  {
    category: "Industrial & Infrastructure",
    options: ["Manufacturing", "Energy / Oil & Gas", "Aerospace & Defense", "Automotive", "Construction / Engineering", "Mining & Resources", "Agriculture", "Supply Chain / Procurement"],
  },
  {
    category: "Commerce & Consumer",
    options: ["Retail", "Consumer Goods / FMCG", "Food & Beverage", "Beauty & Wellness", "Sports & Entertainment", "Travel & Hospitality", "Luxury & Fashion"],
  },
  {
    category: "Media & Telecom",
    options: ["Media & Publishing", "Telecom", "Streaming & Content", "Music & Audio"],
  },
  {
    category: "Services & Professional",
    options: ["Professional Services", "Management Consulting", "Legal Services", "Accounting & Finance", "Staffing & Recruiting", "Architecture & Design"],
  },
  {
    category: "Public Sector & Other",
    options: ["Government / Public Sector", "Non-Profit", "Education (K-12 / Higher Ed)", "Transportation / Mobility", "Real Estate (non-PropTech)", "Logistics"],
  },
];

// Flat list for backward compatibility
export const ALL_INDUSTRIES = INDUSTRY_GROUPS.flatMap(g => g.options);

// ── Target Personas: 30 personas, 7 department groups ──
export const PERSONA_GROUPS = [
  {
    category: "Executive / C-Suite",
    options: ["CEO/Founder", "CTO/VP Engineering", "CRO/VP Sales", "CMO/VP Marketing", "CFO/VP Finance", "Chief of Staff", "Chief People Officer", "General Counsel", "Chief Digital Officer"],
  },
  {
    category: "Revenue & Sales",
    options: ["VP/Director of Sales", "Head of Inside Sales / SDR Manager", "Head of Field Sales", "Senior Account Executive", "Head of Growth", "VP/Director of Revenue Operations"],
  },
  {
    category: "Business Development & Partnerships",
    options: ["VP/Director of Business Development", "Head of Partnerships", "VP/Director of Alliances"],
  },
  {
    category: "Product & Technology",
    options: ["VP Product", "VP/Director of IT", "Head of Digital Transformation", "VP/Director of Data & Analytics"],
  },
  {
    category: "Finance & Strategy",
    options: ["VP/Director of Strategy", "Head of Corp Dev / M&A", "Director of Finance / FP&A", "Portfolio Manager"],
  },
  {
    category: "Investment & Deal Teams",
    options: ["Investment Analyst / Associate", "Managing Director / Principal", "Partner / GP"],
  },
  {
    category: "Operations & Other",
    options: ["Head of Operations", "Head of Procurement", "VP/Director of Customer Success", "CISO", "Head of People"],
  },
];

// Flat list for backward compatibility
export const ALL_PERSONAS = PERSONA_GROUPS.flatMap(g => g.options);

// ── Target Platforms: 6 categories, ~30 platforms ──
// Used for ICP "Target Platforms" multi-select. Names must align with the
// `name` field in /app/backend/services/platform_fingerprints.json so that
// fingerprint hits match user-selected targets exactly.
export const PLATFORM_GROUPS = [
  {
    category: "eCommerce",
    options: ["Shopify", "Shopify Plus", "WooCommerce", "Magento", "BigCommerce", "Salesforce Commerce Cloud"],
  },
  {
    category: "CRM & Sales",
    options: ["Salesforce", "HubSpot", "Pipedrive", "Zendesk"],
  },
  {
    category: "Payments",
    options: ["Stripe", "Adyen", "Braintree", "Square", "PayPal"],
  },
  {
    category: "BNPL",
    options: ["Klarna", "Afterpay", "Clearpay", "Affirm", "Zip", "Revolut Pay Later", "Zilch", "Laybuy", "Payl8r", "DivideBuy", "Splitit"],
  },
  {
    category: "Marketing",
    options: ["Klaviyo", "Marketo", "Mailchimp", "ActiveCampaign", "Iterable", "Customer.io"],
  },
  {
    category: "Infrastructure",
    options: ["AWS", "Google Cloud", "Azure", "Vercel", "Netlify", "Cloudflare"],
  },
  {
    category: "Analytics",
    options: ["Google Analytics", "Segment", "Mixpanel", "Amplitude", "Heap"],
  },
  {
    category: "ERP & Operations",
    options: ["Oracle NetSuite", "SAP", "Microsoft Dynamics 365", "Workday"],
  },
];

export const ALL_PLATFORMS = PLATFORM_GROUPS.flatMap(g => g.options);

// ── Preset suggestions for the free-text ICP fields. Users can still add
// their own values; these exist so the multi-selects aren't empty shells.
export const TECH_STACK_PRESETS = PLATFORM_GROUPS;

export const PAIN_POINT_GROUPS = [
  {
    category: "Revenue & Conversion",
    options: ["High checkout abandonment", "Failed payment recovery", "Low conversion rate", "Pricing pressure / discounting", "Churn and retention loss", "Long sales cycles"],
  },
  {
    category: "Operations & Process",
    options: ["Manual billing processes", "Fragmented tooling", "Manual data entry", "Slow onboarding", "Poor forecasting accuracy", "Limited reporting visibility"],
  },
  {
    category: "Growth & GTM",
    options: ["Expensive customer acquisition", "Weak pipeline coverage", "Cross-border expansion friction", "Low rep productivity", "Thin market intelligence"],
  },
  {
    category: "Risk & Compliance",
    options: ["Fraud and chargebacks", "Regulatory compliance burden", "Security / data residency concerns", "Vendor concentration risk"],
  },
];
export const ALL_PAIN_POINTS = PAIN_POINT_GROUPS.flatMap(g => g.options);

export const USE_CASE_GROUPS = [
  {
    category: "Revenue",
    options: ["Payment optimization", "Revenue recovery", "Checkout conversion uplift", "Subscription billing", "Dynamic pricing", "Upsell and cross-sell"],
  },
  {
    category: "Sales & Marketing",
    options: ["Account research and prioritisation", "Pre-meeting briefing", "Outbound personalisation", "Territory planning", "Partner enablement"],
  },
  {
    category: "Operations",
    options: ["Workflow automation", "Vendor consolidation", "Data enrichment", "Reporting and analytics", "Customer onboarding"],
  },
  {
    category: "Strategy & Diligence",
    options: ["Market mapping", "Competitive benchmarking", "M&A target screening", "Investment diligence", "Expansion planning"],
  },
];
export const ALL_USE_CASES = USE_CASE_GROUPS.flatMap(g => g.options);
